import { useDarkMode } from "../../contexts/darkModeContext";
import React, {useEffect, useState} from "react";
import { useAuth } from "../../contexts/authContext";
import {Button, Input, Select} from "@headlessui/react";
import logo from "../../GROLINKZ_ICON.png"
import {CheckCircleIcon, TrashIcon, MapPinIcon} from "@heroicons/react/24/outline";
import {
    ChevronRightIcon,
} from '@heroicons/react/20/solid'
import {db} from "../../firebase/firebase";


const CourseExpanded = ({ course, onUpdate }) => {
    const { darkMode } = useDarkMode();
    const { currentUser } = useAuth();

    // eslint-disable-next-line
    const [name, setName] = useState(course.name || "");
    // eslint-disable-next-line
    const [size, setSize] = useState(course.size || "default");
    const [holes, setHoles] = useState(course.holes || []);

    const [greensTotal, setGreensTotal] = useState(0);
    const [fairwaysTotal, setFairwaysTotal] = useState(0);
    const [teesTotal, setTeesTotal] = useState(0);
    const [roughTotal, setRoughTotal] = useState(0);

    const [unit, setUnit] = useState(course.unit || "");

    // Function to handle changes in inputs
    const handleInputChange = (index, field, value) => {
        const updatedValues = holes.map((hole, i) =>
            i === index ? { ...hole, [field]: value } : hole
        );
        setHoles(updatedValues);
        // setIsModified(true);
    };

    useEffect(() => {
        const totalGreen = holes.reduce((sum, hole) => sum + (parseFloat(hole.green) || 0), 0);
        const totalFairway = holes.reduce((sum, hole) => sum + (parseFloat(hole.fairway) || 0), 0);
        const totalTee = holes.reduce((sum, hole) => sum + (parseFloat(hole.tee) || 0), 0);
        const totalRough = holes.reduce((sum, hole) => sum + (parseFloat(hole.rough) || 0), 0);

        setGreensTotal(totalGreen);
        setFairwaysTotal(totalFairway);
        setTeesTotal(totalTee);
        setRoughTotal(totalRough);
    }, [holes]);



    // If course is null or undefined, show an error message or loading state
    if (!course) {
        return (
            <div className={`p-3 border h-full ${darkMode ? 'text-white' : 'text-black'}`}>
                <p className="text-red-500">No course selected.</p>
            </div>
        );
    }

    const handleUpdateCourse = async () => {
        const collectionRef = db.collection('Users').doc(currentUser.uid).collection('courses').doc(course.id);

        const courseData = {
            holes: holes,
            greensTotal: greensTotal,
            fairwaysTotal: fairwaysTotal,
            teesTotal: teesTotal,
            roughTotal: roughTotal,
        };

        try {
            await collectionRef.update(courseData);
            // alert("Course updated successfully!");
            // setAdd(false);
            onUpdate();
        } catch (error) {
            console.error("Error updating course:", error);
            // alert("There was an error adding the course.");
        }
    };

    return (
        <div className={`relative h-full space-y-4 ${darkMode ? 'text-white' : 'text-black'} overflow-hidden`}>

            {/*Header*/}
            <div className={`hidden flex items-center`}>

                <p className="mt-6 font-semibold text-xl tracking-widest mb-2">Course Details: <span
                    className={`text-gray-500`}>{name}</span></p>

                {/*Unit*/}
                <div className="ml-auto relative">
                    <label
                        className={`absolute ${darkMode ? 'bg-bgDark' : 'bg-white'} -top-2 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                    >
                        Unit
                    </label>
                    <Select
                        placeholder="Enter Course Size..."
                        required
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className={`block w-full ${darkMode ? 'bg-bgDark outline-neutral-700' : 'bg-white outline-gray-300'} rounded-md px-3 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                    >
                        <option value="">Select Area Unit...</option>
                        <option value="Acres">Acres</option>
                        <option value="Sqft">Sqft</option>
                    </Select>
                </div>

            </div>


            <div className="space-y-2">

                {/* Page heading */}
                <header className="">
                    <div className="mx-auto px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">

                        {/*Title*/}
                        <div className="min-w-0 flex-1">
                            <nav aria-label="Breadcrumb" className="flex">
                                <ol className="flex items-center space-x-4">
                                    <li>
                                        <div className="text-sm font-medium text-gray-500 hover:text-gray-700">
                                            Courses
                                        </div>
                                    </li>
                                    <li>
                                        <div className="flex items-center">
                                            <ChevronRightIcon aria-hidden="true" className="size-5 shrink-0 text-gray-400"/>
                                            <div
                                               className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                                                {size} Hole
                                            </div>
                                        </div>
                                    </li>
                                </ol>
                            </nav>
                            <h1 className="mt-2 text-2xl/7 tracking-widest font-bold sm:truncate sm:text-3xl">
                                {name}
                            </h1>
                            <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8">
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <MapPinIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Greens: <span className={`${darkMode ? 'text-white' : 'text-black'} px-2`}>{greensTotal}</span> ac
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <MapPinIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Fairways: <span className={`${darkMode ? 'text-white' : 'text-black'} px-2`}>{fairwaysTotal}</span> ac
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <MapPinIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Tees: <span className={`${darkMode ? 'text-white' : 'text-black'} px-2`}>{teesTotal}</span> ac
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <MapPinIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Rough: <span className={`${darkMode ? 'text-white' : 'text-black'} px-2`}>{roughTotal}</span> ac
                                </div>
                            </div>
                        </div>

                        {/*Buttons*/}
                        <div className="mt-5 flex xl:ml-4 xl:mt-0">
                            <span className="hidden sm:block">
                                <Button
                                    type="button"
                                    className={`inline-flex items-center gap-x-1.5 rounded-md hover:text-red-600 hover:animate-pulse px-3 py-1.5 text-sm font-semibold hover:bg-opacity-50`}
                                >
                                  <TrashIcon aria-hidden="true" className="-ml-0.5 size-5 text-red-600"/>
                                  Delete
                                </Button>
                            </span>

                            <span className="ml-3 hidden sm:block">
                                <Button
                                    type="button"
                                    onClick={handleUpdateCourse}
                                    className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-opacity-50"
                                >
                                  <CheckCircleIcon aria-hidden="true" className="-ml-0.5 size-5"/>
                                  Save
                                </Button>
                            </span>

                        </div>

                    </div>
                </header>

                {/*Holes*/}
                <main className="pt-2 h-[calc(100dvh-13rem)] overflow-scroll scrollbar-none">
                    <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
                        <div className="px-4 sm:px-0">
                            <h2 className="text-lg font-medium">Holes</h2>
                        </div>

                        {/* Stacked list */}
                        <ul className={`mt-5 divide-y ${darkMode ? 'divide-neutral-700' : 'divide-gray-200'} border-t border-gray-200 sm:mt-0 sm:border-t-0`}>
                            {holes.map((hole, index) => (
                                <li key={index}>
                                    <div className="group block">
                                        <div className="flex items-center px-4 py-3 sm:px-0 sm:py-3">
                                            <div className="flex min-w-0 flex-1 items-center">
                                                <div className="shrink-0">
                                                    <img
                                                        alt=""
                                                        src={logo}
                                                        className="w-12 group-hover:opacity-75"
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1 px-4 md:grid md:grid-cols-1 md:gap-4">
                                                    <div>
                                                        <p className="truncate text-sm font-medium text-green-600">{hole.name}</p>
                                                    </div>


                                                    <div className={`grid grid-cols-4 gap-3`}>

                                                        {/*Greens*/}
                                                        <div className="col-span-1 relative flex items-center justify-center">
                                                            <label
                                                                className={`absolute ${darkMode ? 'bg-bgDark' : 'bg-white'} -top-1.5 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                                                            >
                                                                Green Size
                                                            </label>
                                                            <div
                                                                className={`w-full flex items-center ${darkMode ? 'bg-bgDark outline-neutral-700' : 'bg-white outline-gray-300'} rounded-md outline outline-1 -outline-offset-1 px-3`}>
                                                                <Input
                                                                    type="number"
                                                                    placeholder="Enter Green Size..."
                                                                    required
                                                                    value={hole.green}
                                                                    onChange={(e) => handleInputChange(index, 'green', e.target.value)}
                                                                    className={`block w-full ${darkMode ? 'bg-bgDark' : 'bg-white'} rounded-md py-1.5 text-base placeholder:text-gray-400 focus:outline-none sm:text-xs/5`}
                                                                />
                                                                <div
                                                                    className="shrink-0 select-none text-base text-gray-500 sm:text-xs/6 pl-2">
                                                                    {unit || "Unit"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/*Fairway*/}
                                                        <div className="col-span-1 relative flex items-center justify-center">
                                                            <label
                                                                className={`absolute ${darkMode ? 'bg-bgDark' : 'bg-white'} -top-1.5 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                                                            >
                                                                Fairway Size
                                                            </label>
                                                            <div
                                                                className={`w-full flex items-center ${darkMode ? 'bg-bgDark outline-neutral-700' : 'bg-white outline-gray-300'} rounded-md outline outline-1 -outline-offset-1 px-3`}>
                                                                <Input
                                                                    type="number"
                                                                    placeholder="Enter Fairway Size..."
                                                                    required
                                                                    value={hole.fairway}
                                                                    onChange={(e) => handleInputChange(index, 'fairway', e.target.value)}
                                                                    className={`block w-full ${darkMode ? 'bg-bgDark' : 'bg-white'} rounded-md py-1.5 text-base placeholder:text-gray-400 focus:outline-none sm:text-xs/5`}
                                                                />
                                                                <div
                                                                    className="shrink-0 select-none text-base text-gray-500 sm:text-xs/6 pl-2">
                                                                    {unit || "Unit"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/*Tee*/}
                                                        <div className="col-span-1 relative flex items-center justify-center">
                                                            <label
                                                                className={`absolute ${darkMode ? 'bg-bgDark' : 'bg-white'} -top-1.5 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                                                            >
                                                                Tee Size
                                                            </label>
                                                            <div
                                                                className={`w-full flex items-center ${darkMode ? 'bg-bgDark outline-neutral-700' : 'bg-white outline-gray-300'} rounded-md outline outline-1 -outline-offset-1 px-3`}>
                                                                <Input
                                                                    type="number"
                                                                    placeholder="Enter Tee Size..."
                                                                    required
                                                                    value={hole.tee}
                                                                    onChange={(e) => handleInputChange(index, 'tee', e.target.value)}
                                                                    className={`block w-full ${darkMode ? 'bg-bgDark' : 'bg-white'} rounded-md py-1.5 text-base placeholder:text-gray-400 focus:outline-none sm:text-xs/5`}
                                                                />
                                                                <div
                                                                    className="shrink-0 select-none text-base text-gray-500 sm:text-xs/6 pl-2">
                                                                    {unit || "Unit"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/*Rough*/}
                                                        <div className="col-span-1 relative flex items-center justify-center">
                                                            <label
                                                                className={`absolute ${darkMode ? 'bg-bgDark' : 'bg-white'} -top-1.5 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                                                            >
                                                                Rough Size
                                                            </label>
                                                            <div
                                                                className={`w-full flex items-center ${darkMode ? 'bg-bgDark outline-neutral-700' : 'bg-white outline-gray-300'} rounded-md outline outline-1 -outline-offset-1 px-3`}>
                                                                <Input
                                                                    type="number"
                                                                    placeholder="Enter Rough Size..."
                                                                    required
                                                                    value={hole.rough}
                                                                    onChange={(e) => handleInputChange(index, 'rough', e.target.value)}
                                                                    className={`block w-full ${darkMode ? 'bg-bgDark' : 'bg-white'} rounded-md py-1.5 text-base placeholder:text-gray-400 focus:outline-none sm:text-xs/5`}
                                                                />
                                                                <div
                                                                    className="shrink-0 select-none text-base text-gray-500 sm:text-xs/6 pl-2">
                                                                    {unit || "Unit"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </div>


                                                </div>
                                            </div>
                                            <div>
                                                <ChevronRightIcon
                                                    aria-hidden="true"
                                                    className="size-5 text-gray-400 group-hover:text-gray-700"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                    </div>
                </main>

            </div>


        </div>
    );
};

export default CourseExpanded;
